(() => {
  if (window.BMGBreathOrb) return;

  const GEARS = {
    baseline: { name: 'Baseline', seconds: 3, label: '3 in / 3 out' },
    upshift: { name: 'Upshift', seconds: 2, label: '2 in / 2 out' },
    overdrive: { name: 'Overdrive', seconds: 1, label: '1 in / 1 out' },
    downshift: { name: 'Downshift', seconds: 4, label: '4 in / 4 out' },
    turbo: { name: 'Turbo', seconds: 1, label: '1 in / 1 out' }
  };
  const controllers = new WeakMap();
  const decodedMaps = new WeakMap();

  const gearFor = (value) => GEARS[String(value || 'Baseline').toLowerCase()] || GEARS.baseline;
  const ease = (value) => value * value * (3 - (2 * value));

  class BreathOrbController {
    constructor(orb, audio) {
      this.orb = orb;
      this.audio = audio;
      this.cue = orb.querySelector('[data-breath-orb-cue]');
      this.pace = orb.querySelector('[data-breath-orb-pace]');
      this.frame = 0;
      this.lastCue = '';
      this.timeline = null;
      this.timelineSignature = 'default';
      this.breathMap = null;
      this.mapSignature = 'default';
      this.energyData = null;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.render = this.render.bind(this);
      this.start = this.start.bind(this);
      this.pause = this.pause.bind(this);
      this.complete = this.complete.bind(this);
      this.syncReducedMotionState = this.syncReducedMotionState.bind(this);
      this.setGear(orb.dataset.gear || 'Baseline');
      audio.addEventListener('play', this.start);
      audio.addEventListener('pause', this.pause);
      audio.addEventListener('ended', this.complete);
      if (this.reducedMotion) audio.addEventListener('timeupdate', this.syncReducedMotionState);
      this.rest(audio.currentTime > 0 ? 'Paused' : 'Ready');
      if (!audio.paused) this.start();
    }

    setGear(value) {
      this.gear = gearFor(value);
      this.orb.dataset.gear = this.gear.name;
      this.orb.style.setProperty('--bmg-orb-cycle', `${this.gear.seconds * 2}s`);
      if (this.pace) this.pace.textContent = `${this.gear.name} / ${this.gear.label}`;
    }

    setTimeline(value) {
      const start = Number(value?.start);
      const count = Number(value?.count);
      const hasStart = Number.isFinite(start) && start >= 0;
      const hasCount = Number.isFinite(count) && count > 0;
      const signature = hasStart ? `${start}:${hasCount ? count : 'open'}` : 'default';
      if (signature === this.timelineSignature) return;
      this.timelineSignature = signature;
      this.timeline = hasStart ? { start, count: hasCount ? count : null } : null;
      if (this.reducedMotion) this.syncReducedMotionState();
    }

    setBreathMap(value) {
      const segments = value?.segments?.map(segment => `${segment.start}:${segment.end}`).join('|') || '';
      const signature = value ? `${value.duration}:${value.events}:${value.energy?.length || 0}:${segments}` : 'default';
      if (signature === this.mapSignature) return;
      this.mapSignature = signature;
      this.breathMap = value || null;
      this.energyData = value ? decodeMap(value) : null;
      this.orb.dataset.motion = value ? 'mapped' : 'paced';
      this.orb.style.setProperty('--bmg-orb-energy', '0');
      if (this.reducedMotion) this.syncReducedMotionState();
    }

    setCue(value) {
      if (value === this.lastCue) return;
      this.lastCue = value;
      if (this.cue) this.cue.textContent = value;
    }

    rest(label) {
      window.cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.orb.classList.remove('is-breathing');
      this.orb.dataset.phase = 'rest';
      this.orb.style.setProperty('--bmg-orb-scale', '.82');
      this.orb.style.setProperty('--bmg-orb-energy', '0');
      this.setCue(label);
    }

    start() {
      if (this.reducedMotion) {
        this.syncReducedMotionState();
        return;
      }
      if (this.frame) return;
      this.orb.classList.add('is-breathing');
      this.render();
    }

    pause() {
      if (this.audio.ended) return;
      this.rest(this.audio.currentTime > 0 ? 'Paused' : 'Ready');
    }

    complete() { this.rest('Complete'); }

    timelineState() {
      if (this.breathMap) {
        if (this.breathMap.segments?.length) {
          if (this.audio.currentTime < this.breathMap.segments[0].start) return 'ready';
          const active = this.breathMap.segments.some(
            segment => this.audio.currentTime >= segment.start && this.audio.currentTime < segment.end
          );
          return active ? 'breathing' : 'resting';
        }
        const start = this.breathMap.onsets?.[0] ?? 0;
        if (this.audio.currentTime < start) return 'ready';
        return this.audio.currentTime < this.breathMap.duration ? 'breathing' : 'resting';
      }
      if (!this.timeline) return 'breathing';
      if (this.audio.currentTime < this.timeline.start) return 'ready';
      const end = this.timeline.count
        ? this.timeline.start + (this.timeline.count * this.gear.seconds * 2)
        : Number.POSITIVE_INFINITY;
      return this.audio.currentTime < end ? 'breathing' : 'complete';
    }

    syncReducedMotionState() {
      const state = this.timelineState();
      this.orb.classList.toggle('is-breathing', state === 'breathing' && !this.audio.paused);
      this.orb.dataset.phase = state === 'breathing' ? 'inhale' : 'rest';
      this.orb.style.setProperty('--bmg-orb-scale', state === 'breathing' ? '.9' : '.82');
      this.orb.style.setProperty('--bmg-orb-energy', '0');
      this.setCue(state === 'ready' ? 'Ready' : state === 'complete' ? 'Complete' : state === 'resting' ? 'Rest' : 'Breathing');
    }

    mappedFrame() {
      const time = this.audio.currentTime;
      const onsets = this.breathMap.onsets;
      const segment = this.breathMap.segments?.find(item => time >= item.start && time < item.end);
      const eventStart = segment?.eventStart ?? 0;
      const eventEnd = segment?.eventEnd ?? onsets.length;
      let low = eventStart;
      let high = eventEnd;
      while (low < high) {
        const middle = (low + high) >> 1;
        if (onsets[middle] <= time) low = middle + 1;
        else high = middle;
      }
      const phaseIndex = Math.max(eventStart, low - 1);
      const start = onsets[phaseIndex];
      const end = phaseIndex + 1 < eventEnd ? onsets[phaseIndex + 1] : segment?.end ?? this.breathMap.duration;
      const progress = Math.min(1, Math.max(0, (time - start) / Math.max(.01, end - start)));
      const smoothed = ease(progress);
      const inhaling = (phaseIndex - eventStart) % 2 === 0;
      const scale = inhaling ? .78 + (.22 * smoothed) : 1 - (.22 * smoothed);
      const energyPosition = Math.max(0, time * this.breathMap.rate);
      const energyIndex = Math.min(this.energyData.length - 1, Math.floor(energyPosition));
      const nextEnergyIndex = Math.min(this.energyData.length - 1, energyIndex + 1);
      const energyMix = energyPosition - energyIndex;
      const energy = ((this.energyData[energyIndex] * (1 - energyMix)) + (this.energyData[nextEnergyIndex] * energyMix)) / 255;
      this.orb.dataset.phase = inhaling ? 'inhale' : 'exhale';
      this.orb.style.setProperty('--bmg-orb-scale', scale.toFixed(4));
      this.orb.style.setProperty('--bmg-orb-energy', energy.toFixed(4));
      this.setCue(inhaling ? 'Inhale' : 'Exhale');
    }

    render() {
      if (this.audio.paused || this.audio.ended) {
        this.frame = 0;
        return;
      }
      const timelineState = this.timelineState();
      if (timelineState !== 'breathing') {
        this.orb.classList.remove('is-breathing');
        this.orb.dataset.phase = 'rest';
        this.orb.style.setProperty('--bmg-orb-scale', timelineState === 'ready' ? '.78' : '.82');
        this.orb.style.setProperty('--bmg-orb-energy', '0');
        this.setCue(timelineState === 'ready' ? 'Ready' : timelineState === 'resting' ? 'Rest' : 'Complete');
        this.frame = window.requestAnimationFrame(this.render);
        return;
      }
      this.orb.classList.add('is-breathing');
      if (this.breathMap) {
        this.mappedFrame();
        this.frame = window.requestAnimationFrame(this.render);
        return;
      }
      const halfCycle = this.gear.seconds;
      const elapsed = this.audio.currentTime - (this.timeline?.start || 0);
      const position = (elapsed % (halfCycle * 2)) / halfCycle;
      const inhaling = position < 1;
      const progress = inhaling ? position : position - 1;
      const smoothed = ease(Math.min(1, Math.max(0, progress)));
      const scale = inhaling ? .78 + (.22 * smoothed) : 1 - (.22 * smoothed);
      this.orb.dataset.phase = inhaling ? 'inhale' : 'exhale';
      this.orb.style.setProperty('--bmg-orb-scale', scale.toFixed(4));
      this.setCue(inhaling ? 'Inhale' : 'Exhale');
      this.frame = window.requestAnimationFrame(this.render);
    }
  }

  const decodeMap = (map) => {
    if (decodedMaps.has(map)) return decodedMaps.get(map);
    const binary = window.atob(map.energy || '');
    const values = Uint8Array.from(binary, (character) => character.charCodeAt(0));
    decodedMaps.set(map, values);
    return values;
  };

  const findAudio = (orb) => {
    const scope = orb.closest('[data-audio-player], [data-start-player], [data-free-player], .bmg-audio-sample__inner, .bmg-audio');
    return scope?.querySelector('audio') || null;
  };

  const attach = (orb, audio = findAudio(orb)) => {
    if (!orb || !audio || orb.hasAttribute('data-orb-ambient')) return null;
    if (controllers.has(orb)) return controllers.get(orb);
    const controller = new BreathOrbController(orb, audio);
    controllers.set(orb, controller);
    return controller;
  };

  const setGear = (orb, gear) => {
    if (!orb) return;
    orb.dataset.gear = gearFor(gear).name;
    const controller = controllers.get(orb) || attach(orb);
    controller?.setGear(gear);
  };

  const setTimeline = (orb, timeline) => {
    if (!orb) return;
    const controller = controllers.get(orb) || attach(orb);
    controller?.setTimeline(timeline);
  };

  const setBreathMap = (orb, map) => {
    if (!orb) return;
    const value = typeof map === 'string' ? window.BMGBreathMaps?.[map] : map;
    const controller = controllers.get(orb) || attach(orb);
    controller?.setBreathMap(value || null);
  };

  const init = (scope = document) => {
    scope.querySelectorAll('[data-breath-orb]:not([data-orb-ambient])').forEach((orb) => attach(orb));
  };

  window.BMGBreathOrb = { attach, get: (orb) => controllers.get(orb) || null, init, setBreathMap, setGear, setTimeline };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  else init();
})();
